import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, OnDestroy, inject } from '@angular/core';
import { MenuService } from '../../services/menu.service';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { HeaderService } from '../../services/header.service';
import { AuthService } from '../../services/auth.services';
import { SimulationService } from '../../services/simulation.service';

export interface MenuItem {
  icon?: string;
  label: string;
  route?: string;
  url?: string;
  active: boolean;
  hide?: boolean;
  disabled?: boolean;
  children: MenuItem[];
}

@Component({
  selector: 'app-menu',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './menu.html',
  styleUrls: ['./menu.scss'],
})
export class Menu implements OnInit, OnDestroy {
  @Input() logo!: string;
  @Input() title!: string;

  showMenu = false;

  private menuService = inject(MenuService);
  private router = inject(Router);
  private authService = inject(AuthService);
  private headerService = inject(HeaderService);
  private simulationService = inject(SimulationService);
  private menuSubscription: Subscription | null = null;
  private profileSubscription: Subscription | null = null;
  private simulationSubscription: Subscription | null = null;
  private isSimulating = false;

  ngOnInit(): void {
    if (this.authService.isAuthenticated()) {
      if (sessionStorage.getItem('menu')) {
        try {
          this.data = JSON.parse(sessionStorage.getItem('menu')!);
        } catch {
          this.data = [];
        }
      }

      if (!this.data || this.data.length === 0) {
        this.loadMenuFromServer();
      }
    } else {
      this.data = [];
      sessionStorage.removeItem('menu');
    }

    this.menuSubscription = this.menuService.menuSubject.subscribe({
      next: (menu: MenuItem[]) => {
        this.data = menu;
        this.aplicarEstadoSimulacao();
      },
    });

    this.simulationSubscription = this.simulationService.simulationState$.subscribe({
      next: (state) => {
        this.isSimulating = state.active;
        this.aplicarEstadoSimulacao();
      },
    });
  }

  ngOnDestroy(): void {
    if (this.menuSubscription) {
      this.menuSubscription.unsubscribe();
      this.menuSubscription = null;
    }

    if (this.profileSubscription) {
      this.profileSubscription.unsubscribe();
      this.profileSubscription = null;
    }

    if (this.simulationSubscription) {
      this.simulationSubscription.unsubscribe();
      this.simulationSubscription = null;
    }
  }

  private loadMenuFromServer(): void {
    this.authService.reloadUserData().subscribe({
      next: () => {
        // Dados do usuário e menu recarregados com sucesso
      },
      error: (_error) => {
        this.authService.logout();
      }
    });
  }

  data: MenuItem[] = [];

  externalLinks: { url: string; label: string }[] = [];
  socialMedia: { url: string; icon: string; ariaLabel?: string }[] = [];
  menuInfo?: string;

  toggle() {
    this.showMenu = !this.showMenu;

    if (!this.showMenu) {
      this.resetMenuState();
    }
  }

  private resetMenuState() {
    const resetRec = (node: MenuItem) => {
      node.active = false;
      node.children.forEach(resetRec);
    };
    this.data.forEach(resetRec);
  }

  onClickItem(menuItem: MenuItem, _parent: MenuItem | null | undefined) {
    if (menuItem.disabled) {
      return;
    }

    if (menuItem.children.length > 0) {
      menuItem.active = !menuItem.active;
      return;
    }

    if (menuItem.route) {
      this.router.navigate([menuItem.route]);
      this.showMenu = false;
      return;
    }

    if (menuItem.url) {
      window.open(menuItem.url, '_blank', 'noopener');
      this.showMenu = false;
      return;
    }
  }

  private aplicarEstadoSimulacao(): void {
    if (!this.data || this.data.length === 0) {
      return;
    }

    if (this.isSimulating) {
      const encontrado = this.encontrarItemSimulacao(this.data);

      if (encontrado) {
        encontrado.disabled = true;
      } else {
        // Item não está nos menus do simulado — recuperar do menu original
        const originalMenu = sessionStorage.getItem('originalMenu');
        if (originalMenu) {
          try {
            const menuOriginal: MenuItem[] = JSON.parse(originalMenu);
            const itemOriginal = this.encontrarItemSimulacao(menuOriginal);
            if (itemOriginal) {
              itemOriginal.disabled = true;
              this.data.push(itemOriginal);
            }
          } catch { /* ignora parse inválido */ }
        }
      }
    } else {
      const encontrado = this.encontrarItemSimulacao(this.data);
      if (encontrado) {
        encontrado.disabled = false;
      }
    }
  }

  private encontrarItemSimulacao(items: MenuItem[]): MenuItem | null {
    for (const item of items) {
      if (item.route?.includes('simular-perfil')) {
        return item;
      }
      if (item.children?.length > 0) {
        const found = this.encontrarItemSimulacao(item.children);
        if (found) return found;
      }
    }
    return null;
  }
}
